import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { rgba, darken } from 'polished';
import classNames from 'classnames';

import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import Typography from '../definitions/Typography';
import MessagePropType from '../definitions/MessagePropType';
import renderMessage from '../functions/renderMessage';
import AnchorAnimation from '../styles/AnchorAnimation';


const LEFT = 'left';
const RIGHT = 'right';
const TOP = 'top';
const BOTTOM = 'bottom';

const START = 'start';
const MIDDLE = 'middle';
const END = 'end';

const LOCATION_OPTIONS = [
    LEFT,
    RIGHT,
    TOP,
    BOTTOM,
];

const ALIGNMENT_OPTIONS = [
    START,
    MIDDLE,
    END,
];

function Tooltip({
    message,
    width,
    nubbinLocation,
    nubbinAlignment = MIDDLE,
    children,
    isCompact,
    className,
    ...otherProps
}) {
    const intl = useIntl();

    return (
        <Container
            className={classNames(className, {
                'rh-p-1_25': isCompact,
                'rh-p-2': !isCompact,
            })}
            width={width}
            nubbinLocation={nubbinLocation}
            nubbinAlignment={nubbinAlignment}
            role="tooltip"
            {...renderMessage(message, intl, children)}
            {...otherProps}
        />
    );
}

Tooltip.propTypes = {
    message: MessagePropType,
    width: PropTypes.string.isRequired,
    nubbinLocation: PropTypes.oneOf(LOCATION_OPTIONS).isRequired,
    nubbinAlignment: PropTypes.oneOf(ALIGNMENT_OPTIONS),
    children: PropTypes.node,
    isCompact: PropTypes.bool,
    className: PropTypes.string,
};

// inspired by https://cssarrowplease.com/
const cssArrowBase = css`
    background-color: ${Colours.COCONUT};
    border: solid 1px ${Colours.STONE};
    border-radius: ${Sizes.SPACING.QUARTER};
    box-shadow: 0 2px 11px ${rgba(Colours.STONE, 0.7)};
    line-height: 1.5;
`;

const TRIANGLE_SIZE = '.625rem';

// Customized for top/end & bottom/end positions only to point more accurately
// at QuestionMarkButton in QuestionContainer.
const CUSTOM_NUBBIN_POSITION = '.225rem';

function cssArrowCommonPsuedo(nubbinLocation, nubbinAlignment) {
    const locations = {
        [START]: {
            [TOP]: 'bottom: 100%; left: 1.75rem;',
            [RIGHT]: 'left: 100%; top: 1.75rem;',
            [BOTTOM]: 'top: 100%; left: 1.75rem;',
            [LEFT]: 'right: 100%; top: 1.75rem;',
        },
        [MIDDLE]: {
            [TOP]: 'bottom: 100%; left: 50%;',
            [RIGHT]: 'left: 100%; top: 50%;',
            [BOTTOM]: 'top: 100%; left: 50%;',
            [LEFT]: 'right: 100%; top: 50%;',
        },
        [END]: {
            [TOP]: `bottom: 100%; right: ${CUSTOM_NUBBIN_POSITION}`, // may be overridden in ::before
            [RIGHT]: 'left: 100%; bottom: 1.75rem;',
            [BOTTOM]: `top: 100%; right: ${CUSTOM_NUBBIN_POSITION}`, // may be overridden in ::before
            [LEFT]: 'right: 100%; bottom: 1.75rem;',
        },
    };

    return `
        ${locations[nubbinAlignment][nubbinLocation]};

        border: solid transparent;
        content: "";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
    `;
}

const oppositeBorderColor = {
    [TOP]: 'border-bottom-color',
    [RIGHT]: 'border-left-color',
    [BOTTOM]: 'border-top-color',
    [LEFT]: 'border-right-color',
};

const marginOffset = {
    [TOP]: 'margin-left',
    [RIGHT]: 'margin-top',
    [BOTTOM]: 'margin-left',
    [LEFT]: 'margin-top',
};

function cssArrowAfter(nubbinLocation) {
    return `
        border-color: ${Colours.TRANSPARENT};
        ${oppositeBorderColor[nubbinLocation]}: ${Colours.COCONUT};
        border-width: ${TRIANGLE_SIZE};
        ${marginOffset[nubbinLocation]}: -${TRIANGLE_SIZE};
    `;
}

// This provides the border colour that peeks out from behind the background
// colour provided by ::after
function cssArrowBefore(nubbinLocation, nubbinAlignment) {
    // to counteract an optical illusion, the pointy nubbin is darkened by
    // 10% from STONE: hsl(196deg 18% 78%) vs hsl(196deg 18% 69%)
    return `
        border-color: ${Colours.TRANSPARENT};
        ${oppositeBorderColor[nubbinLocation]}: ${darken(0.1, Colours.STONE)};
        border-width: calc(${TRIANGLE_SIZE} + 1px);
        ${marginOffset[nubbinLocation]}: calc(-${TRIANGLE_SIZE} - 1px);

        // Override the usual nubbin position in special cases
        ${nubbinAlignment === END
        ? nubbinLocation === TOP || nubbinLocation === BOTTOM
            ? `right: calc(${CUSTOM_NUBBIN_POSITION} - 1px)`
            : 'bottom: calc(1.75rem - 1px)'
        : ''};
    `;
}

const Container = styled.div`
    position: relative;
    box-sizing: border-box;
    font-weight: ${Typography.WEIGHTS.REGULAR};
    font-size: ${Sizes.FONTS.XS};

    max-width: ${props => props.width};
    width: ${props => props.width};

    ${cssArrowBase};

    /* the non-workflow part means we need a ::before as well as an ::after because there is an outline */
    ${({ nubbinLocation, nubbinAlignment }) => `
        &::after {
            ${cssArrowCommonPsuedo(nubbinLocation, nubbinAlignment)};
            ${cssArrowAfter(nubbinLocation)};
        }

        &::before {
            ${cssArrowCommonPsuedo(nubbinLocation, nubbinAlignment)};
            ${cssArrowBefore(nubbinLocation, nubbinAlignment)};
        }
    `}

    a {
        ${AnchorAnimation};
    }

    /* Remove any outer margins on children elements (often present on
    typography elements) */
    > *:first-child {
        margin-top: 0;
    }
    > *:last-child {
        margin-bottom: 0;
    }
`;


export {
    LEFT,
    RIGHT,
    TOP,
    BOTTOM,

    START,
    MIDDLE,
    END,

    LOCATION_OPTIONS,
    ALIGNMENT_OPTIONS,
};
export default observer(Tooltip);
